import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useContext } from 'react'
import { MentorProfileContext } from 'src/mentor/context/MentorProfileContext'
import { getMPDataWithParams } from 'src/mentor/services'
import { isCommonResponseSuccessful } from 'src/mentor/helpers/utilityFunctions'
import {
  checkIntoRoomWithLocalStorageApplied,
  peekIntoRoom,
  checkOutRoom,
} from 'src/clinic/components/CheckIn/services'
import { toast } from 'react-toastify'
import {
  getPeekInForClinic,
  addOrUpdatePeekIn,
  updatePeekInActivity,
  removePeekIn,
} from '../utils/peekInManager'

/**
 * Hook to guard actions that require check-in/peek-in
 * @param {Object} bookedEvent - The booked event data
 * @param {Object} options - Optional parameters
 * @param {Object} options.mentorProfileData - Mentor profile data (optional, falls back to context)
 * @param {Array} options.mentorClinicAssociationData - Mentor clinic associations (optional, falls back to context)
 * @returns {Object} Guard functions and state
 */
export function useCheckInGuard(bookedEvent, options = {}) {
  const navigate = useNavigate()
  const context = useContext(MentorProfileContext)
  // Use provided data or fall back to context, handle null context gracefully
  const mentorProfileData = options.mentorProfileData || context?.mentorProfileData || null
  const mentorClinicAssociationData = options.mentorClinicAssociationData || context?.mentorClinicAssociationData || null
  
  const isSupportPortalPath = useMemo(() => {
    if (typeof window === 'undefined') {
      return false
    }
    const pathname = window.location?.pathname || ''
    return pathname.startsWith('/support-home') || pathname.startsWith('/support/')
  }, [])
  const isSupportPortal = Boolean(options.isSupportPortal) || isSupportPortalPath || (!mentorProfileData && !context)
  
  const [checkInPromptOpen, setCheckInPromptOpen] = useState(false)
  const [checkoutPromptOpen, setCheckoutPromptOpen] = useState(false)
  const [checkingIn, setCheckingIn] = useState(false)
  const [availableRooms, setAvailableRooms] = useState([])
  const [selectedRoom, setSelectedRoom] = useState(null)

  // Get clinic location data from booked event
  const clinicLocationData = useMemo(() => {
    if (!bookedEvent?.clinicId || !mentorClinicAssociationData) return null
    return mentorClinicAssociationData.find((clinic) => clinic.id === bookedEvent.clinicId)
  }, [bookedEvent?.clinicId, mentorClinicAssociationData])

  // Get clinic brand data - first try from bookedEvent, then from clinicLocationData
  const clinicBrandData = useMemo(() => {
    // First, try to get it directly from the booked event (most reliable)
    if (bookedEvent?.clinicBrandData) {
      return {
        id: bookedEvent?.clinicBrandId || bookedEvent?.clinicBrandData?.id,
        name: bookedEvent?.clinicBrandData?.brandName || 
              bookedEvent?.clinicBrandData?.title || 
              bookedEvent?.clinicBrandData?.name ||
              'Unknown Brand',
      }
    }

    // Try clinicBrandName directly on bookedEvent
    if (bookedEvent?.clinicBrandName) {
      return {
        id: bookedEvent?.clinicBrandId,
        name: bookedEvent.clinicBrandName,
      }
    }

    // Fallback: try to get from clinicLocationData
    if (clinicLocationData) {
      // Try clinicBrandName directly on clinicLocationData
      if (clinicLocationData?.clinicBrandName) {
        return {
          id: clinicLocationData?.clinicBrandId,
          name: clinicLocationData.clinicBrandName,
        }
      }

      // Try clinicBrandData object
      if (clinicLocationData?.clinicBrandData) {
        return {
          id: clinicLocationData?.clinicBrandId,
          name: clinicLocationData.clinicBrandData?.brandName || 
                clinicLocationData.clinicBrandData?.title || 
                clinicLocationData.clinicBrandData?.name ||
                'Unknown Brand',
        }
      }
    }

    // Try to find in mentorClinicAssociationData
    if (clinicLocationData?.clinicBrandId && mentorClinicAssociationData) {
      const clinicWithBrand = mentorClinicAssociationData.find(
        (clinic) => clinic.clinicBrandId === clinicLocationData.clinicBrandId
      )
      if (clinicWithBrand) {
        // Try clinicBrandName directly
        if (clinicWithBrand?.clinicBrandName) {
          return {
            id: clinicLocationData.clinicBrandId,
            name: clinicWithBrand.clinicBrandName,
          }
        }
        // Try clinicBrandData object
        if (clinicWithBrand?.clinicBrandData) {
          return {
            id: clinicLocationData.clinicBrandId,
            name: clinicWithBrand.clinicBrandData?.brandName || 
                  clinicWithBrand.clinicBrandData?.title || 
                  clinicWithBrand.clinicBrandData?.name ||
                  'Unknown Brand',
          }
        }
      }
    }

    // Last resort: return with just the ID
    if (clinicLocationData?.clinicBrandId || bookedEvent?.clinicBrandId) {
      return {
        id: clinicLocationData?.clinicBrandId || bookedEvent?.clinicBrandId,
        name: 'Unknown Brand',
      }
    }

    return null
  }, [bookedEvent, clinicLocationData, mentorClinicAssociationData])

  // Check if user is checked in or peeked in for this clinic location
  // In support portal, always return false (no check-in functionality)
  const isCheckedInOrPeekedIn = useMemo(() => {
    // Support portal doesn't support check-in
    if (isSupportPortal) {
      return false
    }

    const checkedInDetails = localStorage.getItem('checkedInDetails')

    // Check if checked in to this clinic location
    if (checkedInDetails) {
      try {
        const checkedIn = JSON.parse(checkedInDetails)
        if (checkedIn?.clinicLocationId === bookedEvent?.clinicId) {
          return { isCheckedIn: true, isPeekedIn: false, details: checkedIn }
        }
      } catch (error) {
        console.error('Error parsing checked-in details:', error)
      }
    }

    // Check if peeked in to this clinic location (using new manager)
    const peekedIn = getPeekInForClinic(bookedEvent?.clinicId)
    if (peekedIn) {
      return { isCheckedIn: false, isPeekedIn: true, details: peekedIn }
    }

    return false
  }, [bookedEvent?.clinicId, isSupportPortal])

  // Store pending action callback using ref to avoid stale closures
  const pendingActionRef = useRef(null)
  // Track if rooms have been fetched to prevent double fetching
  const hasFetchedRoomsRef = useRef(false)

  // Fetch available rooms for this clinic location, filtered by eventType
  // In support portal, do nothing
  const fetchAvailableRooms = useCallback(async () => {
    if (isSupportPortal) return
    if (!clinicLocationData?.id) return

    try {
      const response = await getMPDataWithParams(`/api/secure/teacher/associated-chamber-rooms`, {
        clinicLocationId: clinicLocationData.id,
      })

      if (response?.status === 200 && isCommonResponseSuccessful(response?.data?.code)) {
        let rooms = response?.data?.data?.['defined-room-access'] || []
        
        // Filter rooms to only show those that support the booked event's eventType
        if (bookedEvent?.eventType && rooms.length > 0) {
          const filteredRooms = rooms.filter((room) => {
            // Check if room's checkInEventOptions support this eventType
            if (!room?.checkInEventOptions || !Array.isArray(room.checkInEventOptions)) {
              return false
            }
            
            // Check each checkInEventOption to see if it supports this eventType
            return room.checkInEventOptions.some((option) => {
              // Check if option has eventTypes array
              if (option?.eventTypes && Array.isArray(option.eventTypes)) {
                return option.eventTypes.some(
                  (et) => et?.uid === bookedEvent.eventType || et?.id === bookedEvent.eventType
                )
              }
              // Check if option has eventTypeIds array
              if (option?.eventTypeIds && Array.isArray(option.eventTypeIds)) {
                return option.eventTypeIds.includes(bookedEvent.eventType)
              }
              return false
            })
          })
          
          // If filtering resulted in rooms, use them; otherwise show all rooms
          rooms = filteredRooms.length > 0 ? filteredRooms : rooms
        }
        
        setAvailableRooms(rooms)
        // Auto-select first available room
        if (rooms.length > 0) {
          setSelectedRoom((prev) => prev || rooms[0])
        } else {
          setSelectedRoom(null)
        }
      }
    } catch (error) {
      console.error('Error fetching rooms:', error)
      toast.error('Failed to fetch available rooms')
      setAvailableRooms([])
      setSelectedRoom(null)
    }
  }, [clinicLocationData?.id, bookedEvent?.eventType, isSupportPortal])

  // Execute pending action after successful check-in/peek-in
  const executePendingAction = useCallback(() => {
    if (pendingActionRef.current) {
      const action = pendingActionRef.current
      pendingActionRef.current = null
      action()
    }
  }, [])

  // Handle check-in
  // In support portal, do nothing
  const handleCheckIn = useCallback(async (checkInMethod = null) => {
    if (isSupportPortal) return false
    if (!selectedRoom) {
      toast.error('Please select a room')
      return
    }

    // If room is occupied by someone else, only allow peek-in
    if (selectedRoom?.occupied && selectedRoom?.checkedInMentorId !== mentorProfileData?.id) {
      toast.error('This room is occupied. Please use peek-in instead.')
      return false
    }

    // Check if user is checked in to a different clinic and checkout first
    const existingCheckedIn = localStorage.getItem('checkedInDetails')
    if (existingCheckedIn) {
      try {
        const checkedInDetails = JSON.parse(existingCheckedIn)
        // If checking in to a different clinic location, checkout from the previous one
        if (checkedInDetails?.clinicLocationId && checkedInDetails.clinicLocationId !== clinicLocationData?.id) {
          await checkOutRoom(checkedInDetails)
          localStorage.removeItem('checkedInDetails')
          localStorage.removeItem('peekedInRoomDetails')
          localStorage.removeItem('roomDataEventTypes')
          toast.info('Checked out from previous clinic')
        }
      } catch (error) {
        console.error('Error checking out from previous clinic:', error)
      }
    }

    // Determine check-in method
    let method = checkInMethod
    if (!method && selectedRoom?.checkInMethodOptions?.length > 0) {
      // Auto-select first enabled method if none provided
      const firstEnabled = selectedRoom.checkInMethodOptions.find((m) => m?.enabled !== false)
      method = firstEnabled?.id
    }
    if (!method) {
      method = 'check-in-method-in-person' // Default fallback
    }

    setCheckingIn(true)
    try {
      const roomData = {
        ...selectedRoom,
        clinicLocationId: clinicLocationData.id,
        clinicBrandId: clinicLocationData.clinicBrandId,
        checkinMethod: method,
      }

      await checkIntoRoomWithLocalStorageApplied(roomData)
      
      // Log check-in event
      toast.success('Successfully checked in')
      setCheckInPromptOpen(false)
      
      // Execute pending action
      setTimeout(() => {
        executePendingAction()
      }, 100)
      
      return true
    } catch (error) {
      console.error('Error checking in:', error)
      toast.error('Failed to check in')
      return false
    } finally {
      setCheckingIn(false)
    }
  }, [selectedRoom, clinicLocationData, mentorProfileData, executePendingAction, isSupportPortal])

  // Handle peek-in
  // In support portal, do nothing
  const handlePeekIn = useCallback(async () => {
    if (isSupportPortal) return false
    if (!selectedRoom) {
      toast.error('Please select a room')
      return
    }

    setCheckingIn(true)
    try {
      const roomData = {
        ...selectedRoom,
        clinicLocationId: clinicLocationData.id,
        clinicBrandId: clinicLocationData.clinicBrandId,
      }

      const response = await peekIntoRoom(roomData)
      
      if (response?.status === 200) {
        // Use new peek-in manager with expiration
        addOrUpdatePeekIn(roomData)
        toast.success('Successfully peeked in')
        setCheckInPromptOpen(false)
        
        // Execute pending action
        setTimeout(() => {
          executePendingAction()
        }, 100)
        
        return true
      } else {
        toast.error('Failed to peek in')
        return false
      }
    } catch (error) {
      console.error('Error peeking in:', error)
      toast.error('Failed to peek in')
      return false
    } finally {
      setCheckingIn(false)
    }
  }, [selectedRoom, clinicLocationData, executePendingAction, isSupportPortal])

  // Handle checkout
  // In support portal, do nothing
  const handleCheckout = useCallback(async () => {
    if (isSupportPortal) return false
    try {
      const { checkOutRoom } = await import('src/clinic/components/CheckIn/services')
      const checkedInDetails = localStorage.getItem('checkedInDetails')
      
      if (checkedInDetails) {
        const details = JSON.parse(checkedInDetails)
        await checkOutRoom(details)
        localStorage.removeItem('checkedInDetails')
        localStorage.removeItem('roomDataEventTypes')
        
        toast.success('Successfully checked out')
        setCheckoutPromptOpen(false)
        return true
      } else {
        // Clear peek-in for this clinic
        if (bookedEvent?.clinicId) {
          removePeekIn(bookedEvent.clinicId)
        }
        localStorage.removeItem('roomDataEventTypes')
        toast.success('Exited peek-in mode')
        setCheckoutPromptOpen(false)
        return true
      }
    } catch (error) {
      console.error('Error checking out:', error)
      toast.error('Failed to check out')
      return false
    }
  }, [isSupportPortal, bookedEvent?.clinicId])

  // Guard function to check access before performing action
  // In support portal, just execute the action without check-in requirement
  const requireCheckIn = useCallback(
    async (actionCallback, forceCheckIn = false) => {
      // Support portal doesn't require check-in, just execute the action
      if (isSupportPortal) {
        if (actionCallback) {
          actionCallback()
        }
        return
      }

      // If already checked in, proceed (unless forceCheckIn is true)
      if (isCheckedInOrPeekedIn?.isCheckedIn && !forceCheckIn) {
        if (actionCallback) {
          actionCallback()
        }
        return
      }

      // If peeked in but not checked in
      if (isCheckedInOrPeekedIn?.isPeekedIn && !forceCheckIn) {
        // For peeked-in users, proceed with action (unless forceCheckIn is true)
        if (actionCallback) {
          actionCallback()
        }
        return
      }

      // Not checked in, or forceCheckIn is true - store action and show check-in prompt
      if (!clinicLocationData) {
        toast.error('Clinic location information not available')
        return
      }

      // Store the action to execute after check-in
      if (actionCallback) {
        pendingActionRef.current = actionCallback
      }

      // Fetch available rooms only if not already fetched
      if (!hasFetchedRoomsRef.current) {
        await fetchAvailableRooms()
        hasFetchedRoomsRef.current = true
      }
      setCheckInPromptOpen(true)
    },
    [isCheckedInOrPeekedIn, clinicLocationData, fetchAvailableRooms, isSupportPortal],
  )

  // Reset selectedRoom when prompt opens/closes (rooms already fetched in requireCheckIn)
  useEffect(() => {
    if (checkInPromptOpen && clinicLocationData) {
      // Only reset selectedRoom, don't fetch again (already fetched in requireCheckIn)
      setSelectedRoom(null)
    } else if (!checkInPromptOpen) {
      // Clear selection when prompt closes
      setSelectedRoom(null)
      hasFetchedRoomsRef.current = false // Reset flag when dialog closes
    }
  }, [checkInPromptOpen, clinicLocationData])

  return {
    // State
    checkInPromptOpen,
    checkoutPromptOpen,
    checkingIn,
    availableRooms,
    selectedRoom,
    clinicLocationData,
    clinicBrandData,
    isCheckedInOrPeekedIn,

    // Actions
    requireCheckIn,
    handleCheckIn,
    handlePeekIn,
    handleCheckout,
    setCheckInPromptOpen,
    setCheckoutPromptOpen,
    setSelectedRoom,
  }
}
